import { useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import {
  aiPlanFilterBarSx,
  aiPlanFilterFieldSx,
} from "../../../constants/regulationDetailsNavTabStyles";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import { usePaginatedConceptValues } from "../../../hooks/usePaginatedConceptValues";
import {
  fetchConditionalPlanOutputConcepts,
  resolveConceptQueryTargetTypes,
  type ConfigurableConcept,
} from "../../../services/autoTaggingService";
import type { ConceptValue } from "../../../services/regulationsService";

const CONCEPT_SEARCH_DEBOUNCE_MS = 300;
const CONCEPT_PAGE_SIZE = 20;

function capitalizeDisplayLabel(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

type EnhancedTaggingRuleFilterBarProps = Readonly<{
  objectType: string;
  objectSubType: string;
  dimension: string;
  value: string;
  onObjectTypeChange: (objectType: string, objectSubType: string) => void;
  onObjectSubTypeChange: (objectSubType: string) => void;
  onDimensionChange: (dimension: string) => void;
  onValueChange: (value: string) => void;
}>;

export default function EnhancedTaggingRuleFilterBar({
  objectType,
  objectSubType,
  dimension,
  value,
  onObjectTypeChange,
  onObjectSubTypeChange,
  onDimensionChange,
  onValueChange,
}: EnhancedTaggingRuleFilterBarProps) {
  const {
    objectTypeOptions,
    getObjectSubTypeOptions,
    planTypesLoading,
    planTypes,
  } = useAssessmentPlanFilterOptions();

  const [conceptOptions, setConceptOptions] = useState<ConfigurableConcept[]>(
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const targetTypes = resolveConceptQueryTargetTypes({
      objectType,
      objectSubType,
      planTypes,
      fallbackTypeCodes: objectTypeOptions.map((item) => item.code),
    });

    if (targetTypes.length === 0) {
      setConceptOptions([]);
      return () => {
        cancelled = true;
      };
    }

    Promise.all(
      targetTypes.map((type) =>
        fetchConditionalPlanOutputConcepts(type, objectSubType),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        const seen = new Set<string>();
        const merged: ConfigurableConcept[] = [];
        for (const items of results) {
          for (const item of items) {
            const code = item.code.trim();
            if (!code || seen.has(code)) continue;
            seen.add(code);
            merged.push(item);
          }
        }
        setConceptOptions(merged);
      })
      .catch(() => {
        if (!cancelled) setConceptOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [objectSubType, objectType, objectTypeOptions, planTypes]);

  // Empty Object Type → all sub-types across types (hook already aggregates).
  const objectSubTypeOptions = getObjectSubTypeOptions(objectType);
  // Always show Sub-Type by default; hide only when a type has no sub-types (e.g. RTQ).
  const showObjectSubType = !objectType || objectSubTypeOptions.length > 0;

  const dimensionMenuItems = useMemo(
    () =>
      conceptOptions.map((opt) => ({
        code: opt.code,
        name: capitalizeDisplayLabel(opt.name || opt.code),
      })),
    [conceptOptions],
  );

  const allDimensionCodes = useMemo(
    () => dimensionMenuItems.map((item) => item.code).filter(Boolean),
    [dimensionMenuItems],
  );

  const {
    input,
    setInput,
    options,
    loading: valuesLoading,
    hasMoreNext,
    loadingMore,
    loadOptions,
    loadNext,
  } = usePaginatedConceptValues(dimension, CONCEPT_PAGE_SIZE, {
    allowEmptyConceptAsAll: true,
    allConceptCodes: allDimensionCodes,
  });

  const debouncedLoadOptions = useMemo(
    () =>
      debounce((search?: string) => {
        void loadOptions(search);
      }, CONCEPT_SEARCH_DEBOUNCE_MS),
    [loadOptions],
  );

  useEffect(() => () => debouncedLoadOptions.cancel(), [debouncedLoadOptions]);

  const handleValueInputChange = useCallback(
    (_event: unknown, nextValue: string, reason: string) => {
      if (reason === "reset") return;
      setInput(nextValue);
      if (!nextValue.trim()) {
        debouncedLoadOptions.cancel();
        void loadOptions(undefined);
        return;
      }
      debouncedLoadOptions(nextValue);
    },
    [debouncedLoadOptions, loadOptions, setInput],
  );

  const selectedValue = useMemo<ConceptValue | null>(() => {
    if (!value.trim()) return null;
    const matched = options.find((item) => item.code === value);
    return {
      id: value,
      code: value,
      name: matched?.name ?? value,
    };
  }, [options, value]);

  const displayOptions = useMemo(() => {
    if (!selectedValue) return options;
    if (options.some((item) => item.code === selectedValue.code)) return options;
    return [selectedValue, ...options];
  }, [options, selectedValue]);

  return (
    <Box sx={aiPlanFilterBarSx}>
      <TextField
        select
        size="small"
        label="Object Type"
        value={objectType}
        disabled={planTypesLoading}
        sx={aiPlanFilterFieldSx}
        onChange={(event) => {
          // Maps to API `target_type`. Changing type resets `sub_type` to All.
          onObjectTypeChange(event.target.value, "");
        }}
        SelectProps={{
          MenuProps: {
            PaperProps: { sx: { maxHeight: 300 } },
          },
        }}
      >
        <MenuItem value="">All</MenuItem>
        {objectTypeOptions.map((option) => (
          <MenuItem key={option.code} value={option.code}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>

      {showObjectSubType ? (
        <TextField
          select
          size="small"
          label="Object Sub-Type"
          value={objectSubType}
          sx={aiPlanFilterFieldSx}
          onChange={(event) => onObjectSubTypeChange(event.target.value)}
          SelectProps={{
            MenuProps: {
              PaperProps: { sx: { maxHeight: 300 } },
            },
          }}
        >
          <MenuItem value="">All</MenuItem>
          {objectSubTypeOptions.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      <TextField
        select
        size="small"
        label="Dimension"
        value={dimension}
        sx={aiPlanFilterFieldSx}
        onChange={(event) => {
          onDimensionChange(event.target.value);
          onValueChange("");
        }}
        SelectProps={{
          MenuProps: {
            PaperProps: { sx: { maxHeight: 300 } },
          },
        }}
      >
        <MenuItem value="">All</MenuItem>
        {dimensionMenuItems.map((opt) => (
          <MenuItem key={opt.code} value={opt.code}>
            {opt.name}
          </MenuItem>
        ))}
      </TextField>

      <Autocomplete
        size="small"
        options={displayOptions}
        value={selectedValue}
        inputValue={input}
        loading={valuesLoading || loadingMore === "next"}
        getOptionLabel={(option) => option.name || option.code}
        isOptionEqualToValue={(option, selected) =>
          option.code === selected.code
        }
        onChange={(_event, nextValue) => {
          onValueChange(nextValue?.code ?? "");
        }}
        onInputChange={handleValueInputChange}
        sx={{
          ...aiPlanFilterFieldSx,
          width: { xs: "100%", sm: 250 },
          minWidth: 250,
        }}
        ListboxProps={{
          style: { maxHeight: 300 },
          onScroll: (event) => {
            const listbox = event.currentTarget;
            const nearBottom =
              listbox.scrollTop + listbox.clientHeight >=
              listbox.scrollHeight - 40;
            if (nearBottom && hasMoreNext && loadingMore !== "next") {
              void loadNext();
            }
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Value"
            placeholder="Search values..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {valuesLoading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
